"use client"

import { useState, useRef, useEffect } from "react"
import { Search, X, Loader2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import { motion, AnimatePresence } from "framer-motion"
import { useNavigationStore } from "@/store/use-navigation-store"

interface SearchResult {
  id: string
  name: string
  price: number
  image?: string
  category?: string
}

function SearchAutocomplete() {
  const navigate = useNavigationStore((s) => s.navigate)
  const setCategory = useNavigationStore((s) => s.navigate)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setQuery("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      navigate("catalog", { search: query.trim() })
      setQuery("")
      setIsOpen(false)
    }
  }

  const handleChange = (value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (value.trim().length < 2) {
      setResults([])
      setIsOpen(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(`/api/search?search=${encodeURIComponent(value.trim())}`)
        if (res.ok) {
          const data = await res.json()
          setResults(Array.isArray(data) ? data.slice(0, 8) : [])
          setIsOpen(true)
        }
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)
  }

  const handleSelect = (result: SearchResult) => {
    navigate("product", { id: result.id })
    setQuery("")
    setIsOpen(false)
  }

  const handleViewAll = () => {
    if (query.trim()) {
      navigate("catalog", { search: query.trim() })
      setQuery("")
      setIsOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="search"
          placeholder="Поиск дверей..."
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => { if (results.length > 0) setIsOpen(true) }}
          className="pl-9 pr-8"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); setResults([]); setIsOpen(false); inputRef.current?.focus() }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {isOpen && (results.length > 0 || loading) && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 z-50 mt-1 rounded-lg border bg-background shadow-lg overflow-hidden"
          >
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="max-h-80 overflow-y-auto">
                  {results.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleSelect(result)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted transition-colors"
                    >
                      <div className="size-10 shrink-0 rounded-md bg-muted overflow-hidden">
                        {result.image ? (
                          <img src={result.image} alt="" className="size-full object-cover" />
                        ) : (
                          <div className="size-full flex items-center justify-center text-muted-foreground text-xs">Дверь</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{result.name}</p>
                        {result.category && (
                          <p className="text-xs text-muted-foreground">{result.category}</p>
                        )}
                      </div>
                      <span className="text-sm font-bold shrink-0">
                        {result.price.toLocaleString("ru-RU")} ₽
                      </span>
                    </button>
                  ))}
                </div>
                <div className="border-t px-3 py-2">
                  <button
                    onClick={handleViewAll}
                    className="flex w-full items-center justify-between text-sm text-primary hover:underline"
                  >
                    <span>Все результаты по запросу «{query.trim()}»</span>
                    <Search className="size-3.5" />
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export { SearchAutocomplete }
